import React, {
	useMemo,
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import {
	Alert,
	Divider,
	Input,
	Space,
} from 'antd';
import classNames from 'classnames';
import TextArea from 'antd/es/input/TextArea';
import { Moment } from 'moment';
import {
	formatDate,
	getUtcOffset,
	nowMoment,
	toMoment,
} from '@shared/utils/date';
import { round } from '@shared/utils/math';
import HIIHirePeriod from '@shared/hireInvoice/HIIHirePeriod';
import { formatCurrency } from '@shared/utils/currency';
import { groupByProperty } from '@shared/utils/array';
import { getPeriodFromDateForHire } from '@shared/utils/getPeriodFromDate';
import getAvailableHIIsFromTcDetails from '@shared/utils/getAvailableHIIsFromTcDetails';
import getHireCapDate from '@shared/utils/getHireCapDate';
import extendLastDeclaredPeriodToMax from '@shared/utils/extendLastDeclaredPeriodToMax';
import getLastDeclaredPeriod from '@shared/utils/getLastDeclaredPeriod';
import {
	BareboatItemType,
	CurrencySymbols,
	CustomFieldParentTypes,
	CustomFieldType,
	DATE_AND_TIME,
	FixtureTypes,
	HireTypes,
	HireUnit,
	NULL_STRING,
	PaymentTerms,
} from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import HIIExpenseSubjectToHireDays from '@shared/hireInvoice/HIIExpenseSubjectToHireDays';
import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import HIIRevenueSubjectToDays from '@shared/hireInvoice/HIIRevenueSubjectToDays';
import HIIOffHirePeriod from '@shared/hireInvoice/HIIOffHirePeriod';
import getAvailableBbInvoiceItems from '@shared/utils/getAvailableBbInvoiceItems';
import HIIBbItem from '@shared/hireInvoice/HIIBbItem';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import Select from '@client/components/Select';
import {
	createHireInvoice,
	getCustomFields,
	getHireInvoicePreview,
	updateCustomField,
} from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import DatePicker from '@client/components/DatePicker';
import TooltipIcon from '@client/components/TooltipIcon';
import showErrorNotification from '@client/utils/showErrorNotification';
import PdfGenerator from '@client/components/PdfGenerator/PdfGenerator';
import CurrencyInput from '@client/components/CurrencyInput';
import NumericInput from '@client/components/NumericInput';
import Button from '@client/components/Button';
import { SettingsProps } from '@client/components/PdfGenerator/private/Settings';
import useHirePeriodSplit from '@client/screens/fleet/VoyageDetailsScreen/helpers/useHirePeriodSplit';
import Table from '@client/components/Table/Table';
import useBbItemPeriodSplit from '@client/screens/fleet/VoyageDetailsScreen/helpers/useBbItemPeriodSplit';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import { generateVoyageCustomFields } from '@client/screens/management/banking/CustomFields/utils/generateVoyageCustomFields';
import { getBankAccountSetting } from '@client/screens/fleet/VoyageDetailsScreen/helpers/voyageInvoiceForm';
import styles from '../styles/HireInvoiceForm.module.css';
import {
	getItemsWithLinkedReversals,
	updateHIIAttribute,
} from './helpers/invoiceFunctions';

export type Items = HireInvoiceItem[];

type HireInvoiceFormProps = {
	voyageDetails: GetVoyageDetailsResponse;
	fixtureDetails: GetFixtureDetailsResponse<
		TcFixtureProps
	>;
	open: boolean | object;
	isBlankOpen: boolean;
	cumulative: boolean;
	readOnly: boolean;
	onClose: () => void;
	onInvoiceCreated: () => void;
};

const HireInvoiceForm: React.FC<HireInvoiceFormProps> = ({
	voyageDetails: details,
	fixtureDetails,
	open,
	isBlankOpen,
	cumulative,
	readOnly,
	onClose,
	onInvoiceCreated,
}) => {
	const {
		id: voyageId,
		deliveryDate,
		readyForCumulative,
	} = details;

	const { userInfo } = useAuth();
	const hirePeriodEndPickerRef = useRef<HTMLElement>(null);

	// Latest datetime hire can be invoiced to
	// When null user can pick any date
	const hireCapDate = useMemo(
		() => getHireCapDate(details, fixtureDetails.type),
		[details.redeliveryDate, details.isRedeliveryDateEstimated, details.estRedeliveryDate, fixtureDetails.type],
	);

	const hireRateSchedule = useMemo(
		() => (
			fixtureDetails.type === FixtureTypes.TC_OUT ?
				extendLastDeclaredPeriodToMax(details.periodSchedule) :
				details.periodSchedule
		),
		[fixtureDetails.type, details.periodSchedule],
	);

	const declaredHireEnd = useMemo(() => {
		if (fixtureDetails.type !== FixtureTypes.TC_OUT) {
			return null;
		}

		const lastDeclared = getLastDeclaredPeriod(hireRateSchedule);

		return lastDeclared != null ? toMoment(lastDeclared.to) : null;
	}, [fixtureDetails.type, hireRateSchedule]);

	const getHireRatePeriod = useCallback((from: Moment) => {
		if (declaredHireEnd != null && from.isSameOrAfter(declaredHireEnd)) {
			return null;
		}

		return getPeriodFromDateForHire(from, hireRateSchedule);
	}, [declaredHireEnd, hireRateSchedule]);

	const [items, setItems] = useState<Items | []>([]);

	const [showPreviousItems, setShowPreviousItems] = useState<boolean>(false);
	const [showPaymentTerms, setShowPaymentTerms] = useState(true);
	const [showOnBehalfOf, setShowOnBehalfOf] = useState(true);
	const [groupHirePeriods, setGroupHirePeriods] = useState<boolean>(false);
	const [invoiceTitle, setInvoiceTitle] = useState('Hire Invoice');
	const [invoiceTitleTouched, setInvoiceTitleTouched] = useState(false);
	const [includeHirePeriod, setIncludeHirePeriod] = useState<boolean>(!cumulative);
	const [invoiceDate, setInvoiceDate] = useState<Moment>(nowMoment());
	const [isCreditNote, setIsCreditNote] = useState(false);
	const [isCreditNoteTouched, setIsCreditNoteTouched] = useState(false);
	const [useLegacyTime, setUseLegacyTime] = useState(false);
	const [customFieldChange, setCustomFieldChange] = useState(false);
	const [dueDate, setDueDate] = useState<Moment | undefined>(undefined);
	const [manualDueDateOverride, setManualDueDateOverride] = useState(false);
	const [previewTotal, setPreviewTotal] = useState<number | null>(null);
	const [selectedBankAccountId, setSelectedBankAccountId] = useState(-1);

	const isTcIn = fixtureDetails.type === FixtureTypes.TC_IN;
	const canChooseBankAccount = isTcIn && previewTotal != null && previewTotal < 0;
	const usesOwnBankAccount = canChooseBankAccount && selectedBankAccountId > 0;
	const effectiveIsCreditNote = usesOwnBankAccount ? false : isCreditNote;

	useEffect(() => {
		if (usesOwnBankAccount) {
			setIsCreditNote(false);
		}
	}, [usesOwnBankAccount]);

	// Exclude soft-deleted invoices when computing available items — deleted invoices
	// have no line items and should not influence what can be raised in a new invoice.
	const invoices = (details.hireInvoices ?? []).filter((inv) => inv.deletedAt == null);

	const getInvoiceDateBasedDueDate = useCallback((date: Moment) => {
		return date.clone().add(details.tcTerms.paymentTermValue ?? 0, 'days');
	}, [details.tcTerms.paymentTermValue]);

	const isInvoiceDatePaymentTerm =
		details.tcTerms.paymentTerm === PaymentTerms.INVOICE_DATE;

	useEffect(() => {
		if (isInvoiceDatePaymentTerm) {
			setDueDate(getInvoiceDateBasedDueDate(invoiceDate));
		}
	}, [isInvoiceDatePaymentTerm, invoiceDate, getInvoiceDateBasedDueDate]);

	const handleInvoiceDateChange = (newVal: Moment) => {
		setInvoiceDate(newVal);
		if (isInvoiceDatePaymentTerm) {
			setDueDate(getInvoiceDateBasedDueDate(newVal));
			setManualDueDateOverride(false);
		}
	};

	// Only rebuild available items when the drawer is opened (new preset/session).
	// Rebuilding while the drawer is open regenerates ephemeral item ids and breaks drag-and-drop.
	const openSessionKey = useMemo(() => {
		if (!open) {
			return null;
		}

		if (typeof open === 'boolean') {
			return 'open';
		}

		return JSON.stringify(open);
	}, [open]);

	const isHireInvoice = useMemo(() => {
		if (typeof open === 'object' && 'includeHire' in open) {
			return open.includeHire as boolean;
		}

		return true;
	}, [open]);

	const [fields] = useFetchedState(async () => {
		await generateVoyageCustomFields({
			orgId: fixtureDetails.organizationId,
			voyageId,
		});

		return await getCustomFields({
			parentId: fixtureDetails.organizationId,
			parentType: CustomFieldParentTypes.ORG,
			voyageId,
		});
	}, [fixtureDetails?.organizationId, voyageId, customFieldChange]);

	// Refresh available items when the drawer opens (or legacy-time setting changes).
	useEffect(() => {
		if (openSessionKey == null || details.id == null || fixtureDetails.id == null) {
			return;
		}

		setSelectedBankAccountId(-1);
		setPreviewTotal(null);

		let newItems: Items = [];

		if (fixtureDetails.type === FixtureTypes.BB_OUT) {
			newItems = getAvailableBbInvoiceItems({
				hireInvoices: invoices ?? [],
				bankAccount: fixtureDetails.bankAccount,
				bbItems: fixtureDetails.BbItems,
				brokers: fixtureDetails.brokers,
				voyageExpenseReceivables: details.voyageExpenseReceivables,
				fromDate: deliveryDate,
				readyForCumulative,
				completionDate: hireCapDate ?? details.completionDate ?? null,
				useUTC: details.tcTerms.useUTC,
				voyageBunkers: details.voyageBunkers,
				daysDecimals: fixtureDetails.hireDaysDecimals,
			});
		} else {
			newItems = getAvailableHIIsFromTcDetails(
				details,
				fixtureDetails,
				invoices ?? [],
				isHireInvoice,
				useLegacyTime,
			);
		}

		setItems(newItems);
	}, [
		openSessionKey,
		details.id,
		fixtureDetails.id,
		fixtureDetails.type,
		isHireInvoice,
		useLegacyTime,
	]);

	useEffect(() => {
		if (typeof open === 'boolean') {
			return;
		}

		setInvoiceTitleTouched(false);
		setIsCreditNoteTouched(false);

		const presetOptions = {
			title: setInvoiceTitle,
			showPrevious: setShowPreviousItems,
			includeHire: setIncludeHirePeriod,
			isCreditNote: setIsCreditNote,
		};

		Object.entries(open).forEach(([option, value]) => {
			const setOption = presetOptions[option as keyof typeof presetOptions];

			if (setOption == null || value == null) {
				return;
			}

			setOption(value);
		});
	}, [open]);

	useEffect(() => {
		if (!cumulative) {
			return;
		}

		setInvoiceTitle('Cumulative Statement');
	}, [cumulative]);

	const HIIBbItems = useMemo(
		() => items.filter((i) => i.getItemType() === HIIBbItem.itemType) as HIIBbItem[],
		[items],
	);

	const otherItems = useMemo(
		() => items.filter((i) => i.getItemType() !== HIIBbItem.itemType) as HireInvoiceItem[],
		[items],
	);

	const [
		itemsWithHIIBbItems,
		updateHIIBbItemProperty,
	] = useBbItemPeriodSplit(HIIBbItems);

	const HIIBbItemsByType = useMemo(() => {
		const groupedItems: Record<string, HIIBbItem> = {};
		HIIBbItems.forEach((item) => {
			if (item.type) {
				groupedItems[item.type] = item;
			}
		});

		return groupedItems;
	}, [HIIBbItems]);

	const [
		allHirePeriodItems,
		[primaryHirePeriod, secondaryHirePeriod],
		hirePeriodSplitDate,
		setHirePeriodSplitDate,
		updateHirePeriodProperty,
		updateHirePeriodEnd,
		updateExpenseSubjectToHireDays,
	] = useHirePeriodSplit(otherItems);

	const allItems = useMemo(() => [
		...itemsWithHIIBbItems,
		...allHirePeriodItems,
	], [itemsWithHIIBbItems, allHirePeriodItems]);

	const getHirePeriodEndWithStartOffset = (
		startDateTime?: Moment | string | Date | null,
		endDateTime?: Moment | string | Date | null,
		newOffset?: number,
	): Moment | null => {
		if (!startDateTime || !endDateTime) {
			return null;
		}

		const start = startDateTime ? toMoment(startDateTime).clone() : null;
		const end = endDateTime ? toMoment(endDateTime).clone() : null;

		if (!start?.isValid() || !end?.isValid()) {
			return null;
		}

		const fullDays = Math.max(0, Math.floor(end.diff(start, 'days', true)));
		const newEndPeriod = newOffset ? start.clone().add(fullDays, 'days').utcOffset(newOffset) : start.clone().add(fullDays, 'days');

		return newEndPeriod;
	};

	const hirePeriodStart = primaryHirePeriod?.from;

	// The exact stored hire period end (fractional days preserved). The picker snaps this to
	// whole calendar days for display, but the "Days" field and PDF use the real value.
	const rawHirePeriodEnd = (secondaryHirePeriod || primaryHirePeriod)?.to ?
		toMoment((secondaryHirePeriod || primaryHirePeriod).to) :
		null;

	const hirePeriodEnd = getHirePeriodEndWithStartOffset(hirePeriodStart, rawHirePeriodEnd);

	const initialItems = useMemo(() => (cumulative ? [] : allItems.filter((i) => (
		!i.addManually &&
		!i.isAmendment &&
		!i.isReversal &&
		// Remove all hire periods if includeHirePeriod is false
		(includeHirePeriod || !(i instanceof HIIHirePeriod))
	))), [cumulative, includeHirePeriod, allItems]);

	const port = useMemo(
		() => details.tcTerms.deliveryPortsOrRanges?.[0] ?? null,
		[details.tcTerms.deliveryPortsOrRanges],
	);

	const getAddedItems = useCallback(
		(addedItemIds: Array<number | string>) => [
			...initialItems,
			...getItemsWithLinkedReversals(allItems, addedItemIds),
		],
		[initialItems, allItems]
	);
	const getCappedHireEnd = useCallback(() => {
		if (hireCapDate == null) {
			return null;
		}

		return details.tcTerms.useUTC ?
			hireCapDate.clone().utc() :
			hireCapDate.clone().utcOffset(getUtcOffset(port, hireCapDate.toDate()) ?? 0);
	}, [hireCapDate, details.tcTerms.useUTC, port]);

	const save = async (addedItemIds: (number | string)[]) => {
		try {
			await createHireInvoice(
				voyageId,
				getAddedItems(addedItemIds),
				{
					chartererId: fixtureDetails.counterpartyId ?? undefined,
					cumulative,
					showPreviousItems,
					invoiceTitle,
					invoiceDate,
					dueDateOverride:
						isInvoiceDatePaymentTerm && !manualDueDateOverride ?
							getInvoiceDateBasedDueDate(invoiceDate) :
							dueDate,
					groupHirePeriods,
					showPaymentTerms,
					showOnBehalfOf,
					isCreditNote: effectiveIsCreditNote,
					isCargoRelet: false,
					...(canChooseBankAccount ? { fromBankAccountId: selectedBankAccountId } : {}),
				},
			);
			await onInvoiceCreated();
			onClose();
		} catch (e) {
			showErrorNotification('Could not save hire invoice', e as Error);
		}
	};

	const onUpdateInvoice = useCallback((addedItemIds: (number | string)[]) => {
		const addedItems = getAddedItems(addedItemIds);

		const invoiceTotal = addedItems.reduce((acc, item) => (
			acc + item.getTotal(allItems, true)
		), 0);

		// If the user starts from a blank invoice (default title "Invoice") and then adds hire amendments,
		// default to "Hire Invoice" unless they've manually overridden the title.
		if (
			!invoiceTitleTouched &&
			invoiceTitle === 'Invoice' &&
			addedItems.some((i) => i instanceof HIIHirePeriod)
		) {
			setInvoiceTitle('Hire Invoice');
		}

		if (!usesOwnBankAccount) {
			if (invoiceTotal < 0) {
				setIsCreditNote(true);
			} else if (!isCreditNoteTouched) {
				setIsCreditNote(false);
			}
		}
	}, [getAddedItems, allItems, invoiceTitle, invoiceTitleTouched, isCreditNoteTouched, usesOwnBankAccount]);

	const fetchPreview = useCallback(async (
		previewItemIds: Array<number | string>,
	) => {
		const { html, total } = await getHireInvoicePreview(
			details.id,
			getAddedItems(previewItemIds),
			{
				showPreviousItems: showPreviousItems || cumulative,
				showPaymentTerms,
				showOnBehalfOf,
				paymentTerm: details.tcTerms.paymentTerm,
				paymentTermValue: details.tcTerms.paymentTermValue,
				groupHirePeriods,
				invoiceTitle,
				invoiceDate,
				dueDateOverride:
					isInvoiceDatePaymentTerm && !manualDueDateOverride ?
						getInvoiceDateBasedDueDate(invoiceDate) :
						dueDate,
				isCreditNote: effectiveIsCreditNote,
				...(canChooseBankAccount ? { fromBankAccountId: selectedBankAccountId } : {}),
			},
		);

		setPreviewTotal(total);

		return html;
	}, [details.id, getAddedItems, fields, groupHirePeriods, fixtureDetails, showPreviousItems,
		cumulative, invoiceTitle, invoiceDate, dueDate, showPaymentTerms, showOnBehalfOf, effectiveIsCreditNote,
		canChooseBankAccount, selectedBankAccountId, isInvoiceDatePaymentTerm, manualDueDateOverride,
		getInvoiceDateBasedDueDate]);

	const sections = useMemo(() => groupByProperty(
		allItems,
		(i) => ((i.addManually || i.isAmendment || i.isReversal) ? i.templateSection : NULL_STRING),
	), [allItems]);

	const getConfirmStatus = (addedItems: any[]): (null | [null | 'error' | 'warning', string]) => {
		if (cumulative && items.length > addedItems.length) {
			return ['warning', 'Not all items have been added to the invoice. Continue?'];
		}

		if (
			(includeHirePeriod && primaryHirePeriod?.hireRate === 0) ||
			(includeHirePeriod && secondaryHirePeriod != null && secondaryHirePeriod.hireRate === 0)
		) {
			return [
				'warning',
				`Hire per day is set to 
				${CurrencySymbols[fixtureCurrency as keyof typeof CurrencySymbols] || `${fixtureCurrency} `}0. Continue?`
			];
		}

		if (canChooseBankAccount && selectedBankAccountId === -1) {
			return [
				'warning',
				'No bank account selected, so the statement will show no payment details. Continue?',
			];
		}

		return null;
	};

	const fixtureCurrency = details.bankAccount == null ?
		userInfo.baseCurrency :
		details.bankAccount.currency;

	const getExpensesSubjectToHireDaysSettings = () => {
		const hirePeriods = allItems.filter((item) => item instanceof HIIHirePeriod);
		const [filteredHirePeriod] = hirePeriods.map((hirePeriod) => {
			return {
				...hirePeriod,
				children: hirePeriod.children.filter(
					(child) => child instanceof HIIExpenseSubjectToHireDays,
				) as HIIExpenseSubjectToHireDays[],
			};
		});

		if (filteredHirePeriod == null) {
			return [];
		}

		return [
			...filteredHirePeriod.children
				.map((expense) => ({
					label: expense.name,
					content: (
						<CurrencyInput
							currency={fixtureCurrency}
							value={expense.getTotal(allItems) ?? 0}
							onChange={(amount) => updateExpenseSubjectToHireDays(
								filteredHirePeriod.id,
								expense.id,
								amount,
								true,
							)}
						/>
					),
				})),
		];
	};

	const getHirePeriodSettings = (
		primary: boolean,
	) => {
		const hireItem = (primary ? primaryHirePeriod : secondaryHirePeriod) || {};
		const periodScheduleItem = hireItem?.from != null ?
			getHireRatePeriod(toMoment(hireItem.from)) :
			null;

		const hireEndMoment = hireItem.to && toMoment(hireItem.to);
		const rateEndMoment = periodScheduleItem?.to && toMoment(periodScheduleItem.to);

		const exceedsDeclaredPeriod = Boolean(
			hireEndMoment &&
			declaredHireEnd &&
			hireEndMoment.isAfter(declaredHireEnd),
		);

		const overlapWarning = !exceedsDeclaredPeriod && Boolean(
			hireEndMoment &&
			rateEndMoment &&
			hireEndMoment.isAfter(rateEndMoment),
		);

		const exceedsDeclaredPeriodAlert = (
			<Alert
				showIcon
				type="warning"
				message="No hire rate after End (max)"
				description={`
					Hire after ${formatDate(declaredHireEnd, DATE_AND_TIME)} falls outside min./max. declared period.
					Please extend declared hire period.`}
			/>
		);

		const hasError = (
			periodScheduleItem != null &&
			periodScheduleItem.hireRate !== hireItem.hireRate);

		return [
			...(periodScheduleItem == null ? [{
				hide: !includeHirePeriod,
				label: '',
				content: exceedsDeclaredPeriod ? exceedsDeclaredPeriodAlert : (
					<Alert
						showIcon
						type="warning"
						message="No rate schedule found"
						description={`
							The contract specifies a rate schedule, 
							but no rate period corresponding to this hire period (beginning ${formatDate(hireItem.from, DATE_AND_TIME)}) 
						has been found.`}
					/>
				),
			}] : [{
				hide: !includeHirePeriod,
				label: 'Rate Schedule',
				content: (
					<>
						{exceedsDeclaredPeriod && exceedsDeclaredPeriodAlert}
						{overlapWarning && (
							<Alert
								type="warning"
								showIcon
								message="Period is overlapping rates"
								description={`Be aware that the end of the of 
								the hire period, is potentially overlapping multiple rate schedule periods`}
							/>
						)}
						<Table
							pagination={false}
							dataSource={hireRateSchedule}
							size="small"
							rowClassName={(row) => classNames({
								[styles.bold]: periodScheduleItem?.id === row.id,
							})}
							columns={[
								{
									dataIndex: 'from',
									title: 'Start',
									render: (c: number) => (c == null ? (
										<em>Empty</em>
									) : formatDate(c, DATE_AND_TIME)),
								},
								{
									dataIndex: 'to',
									title: 'End',
									render: (c: number) => (c == null ? (
										<em>Empty</em>
									) : formatDate(c, DATE_AND_TIME)),
								},
								{
									dataIndex: 'hireRate',
									title: 'Rate',
									render: (c: number) => (c == null ? (
										<em>Empty</em>
									) : formatCurrency(c, fixtureCurrency)),
								},
							]}
						/>
					</>
				),
			}]),
			{
				hide: !includeHirePeriod,
				label: 'Hire Type',
				content: (
					<Select
						value={hireItem.hireType}
						onChange={(v) => updateHirePeriodProperty(primary, 'hireType', v)}
						options={Object.values(HireTypes).map((v) => ({
							label: capitalize(v),
							value: v,
						}))}
					/>
				),
			},
			{
				hide: !includeHirePeriod,
				label: 'Hire',
				error: hasError || overlapWarning || exceedsDeclaredPeriod,
				content: (
					<Space direction="vertical">
						<CurrencyInput
							currency={fixtureCurrency}
							className={classNames(styles.settingsInput, {
								[styles.missingHire]: includeHirePeriod && hireItem.hireRate === 0,
							})}
							value={hireItem.hireRate}
							onChange={(v) => updateHirePeriodProperty(primary, 'hireRate', v)}
							addonAfter={
								details.tcTerms.hireUnit === HireUnit.MONTHS ?
									'/ month' :
									'/ day'
							}
						/>
						{hasError && (
							<Alert showIcon type="error" message="Hire per day does not match rate schedule" />
						)}
					</Space>
				),
			},
			{
				hide: !includeHirePeriod,
				label: 'Hire description',
				content: (
					<Input
						placeholder={details.tcTerms.hireDescription ?? ''}
						className={styles.settingsInput}
						value={hireItem.hireDescription}
						onChange={(e) => updateHirePeriodProperty(primary, 'hireDescription', e.target.value)}
					/>
				),
			},
		];
	};

	const renderItemContent = (item: HireInvoiceItem) => {
		switch (item.getItemType()) {
			case HIIRevenueSubjectToDays.itemType: {
				const relevantItem = item as HIIRevenueSubjectToDays;

				return (
					<div className={styles.revenueSubjectToDaysContainer}>
						<p className={styles.revenueDescription} style={{ margin: 0 }}>
							{formatCurrency(relevantItem.amount, relevantItem.fixtureCurrency)}
							{' '}
							per day
							{relevantItem.isAmendment ? ' (Amendment)' : ''}
						</p>
						<div className={styles.revenueDateContainer}>
							<div>
								<b className={styles.revenueDatesLabel}>Start</b>
								<DatePicker
									defaultValue={isHireInvoice ? relevantItem.startDate : undefined}
									time
									stopPropagation
									allowClear={false}
									maxDate={relevantItem.endDate}
									onClick={(e) => {
										e.stopPropagation();
									}}
									onChange={(v) => {
										updateHIIAttribute(relevantItem, 'startDate', v, setItems);
									}}
								/>
							</div>
						</div>
						<div className={styles.revenueDateContainer}>
							<div>
								<b className={styles.revenueDatesLabel}>End</b>
								<DatePicker
									defaultValue={isHireInvoice ? relevantItem.endDate : undefined}
									time
									stopPropagation
									allowClear={false}
									minDate={relevantItem.startDate}
									onClick={(e) => {
										e.stopPropagation();
									}}
									onChange={(v) => {
										updateHIIAttribute(relevantItem, 'endDate', v, setItems);
									}}
								/>
							</div>
						</div>
						<div>
							<b>Total:</b>
							{' '}
							{formatCurrency(relevantItem.calculateAmount(), relevantItem.fixtureCurrency)}
						</div>
					</div>
				);
			}

			case HIIOffHirePeriod.itemType: {
				const { type } = (item as HIIOffHirePeriod);

				return (
					<>
						{(type != null) && (
							<>
								<span>
									<span style={{ fontWeight: 'bold' }}>Type: </span>
									{type}
								</span>

							</>
						)}
						<p className={styles.itemDescription}>{item.getDescription(items)}</p>
						<span>
							{formatCurrency(Math.abs(item.getTotal(items, true)), fixtureCurrency)}
						</span>
					</>
				);
			}

			default:
				return (
					<>
						<p className={styles.itemDescription}>{item.getDescription(items)}</p>
						<span>
							{formatCurrency(Math.abs(item.getTotal(items, true)), fixtureCurrency)}
						</span>
					</>
				);
		}
	};

	const fromDateHireRate = useMemo(() => {
		const hireItem = primaryHirePeriod;

		if (hireItem == null) {
			return null;
		}

		return getHireRatePeriod(toMoment(hireItem.from));
	}, [primaryHirePeriod, getHireRatePeriod]);

	const firstDeliveryPort = details.tcTerms.deliveryPortsOrRanges?.[0];
	const date = hirePeriodSplitDate ? toMoment(hirePeriodSplitDate).toDate() : undefined;

	const defaultUtcOffset = details.tcTerms.useUTC ?
		0 :
		(getUtcOffset(firstDeliveryPort, date) ?? 0);
	const endDateCandidate = secondaryHirePeriod?.to ?? primaryHirePeriod?.to;
	const maxDate = endDateCandidate ? toMoment(endDateCandidate) : undefined;
	const minDate = hirePeriodSplitDate ?? hirePeriodStart;

	let splitHirePeriodValue = hirePeriodSplitDate;

	if (hirePeriodSplitDate && details.tcTerms.useUTC) {
		splitHirePeriodValue = hirePeriodSplitDate.clone().utc();
	}

	const settingsSections: SettingsProps['settings'] = {
		General: [
			{
				hide: cumulative,
				label: 'Title',
				content: (
					<Input
						value={invoiceTitle}
						onChange={(e) => {
							setInvoiceTitleTouched(true);
							setInvoiceTitle(e.target.value);
						}}
					/>
				),
			},
			{
				hide: cumulative,
				label: 'Invoice Date',
				content: (
					<DatePicker
						allowClear={false}
						showNow={false}
						className={styles.settingsInput}
						value={invoiceDate}
						onChange={(newVal) => handleInvoiceDateChange(newVal as Moment)}
					/>
				),
			},
			{
				hide: cumulative,
				label: 'Due Date',
				content: (
					<DatePicker
						allowClear={false}
						showNow={false}
						className={styles.settingsInput}
						value={
							isInvoiceDatePaymentTerm && !manualDueDateOverride ?
								getInvoiceDateBasedDueDate(invoiceDate) :
								dueDate
						}
						onChange={(newVal) => {
							setDueDate(newVal as Moment);
							setManualDueDateOverride(true);
						}}
					/>
				),
			},
			{
				hide: cumulative,
				label: 'Include hire period',
				content: (
					<Select
						disabled={isBlankOpen}
						value={includeHirePeriod}
						onChange={(v) => setIncludeHirePeriod(v as boolean)}
						options={[
							{ label: 'Include', value: true },
							{ label: 'Do not include', value: false },
						]}
					/>
				),
			},
			{
				hide: cumulative || !includeHirePeriod,
				label: 'Hire period end',
				content: (
					<>
						<DatePicker
							key={`hire-end-${hirePeriodEnd?.toString()}`}
							ref={hirePeriodEndPickerRef}
							allowClear={false}
							time
							showNow={false}
							className={styles.settingsInput}
							dropdownClassName={styles.hireEndDatePicker}
							value={
								hirePeriodEnd ?
									(details.tcTerms.useUTC ? hirePeriodEnd.clone().utc() : hirePeriodEnd) :
									null
							}
							defaultUtcOffset={details.tcTerms.useUTC ? 0 : getUtcOffset(port, hirePeriodEnd?.toDate())}
							onChange={(v) => {
								if (!v) {
									return;
								}

								const m = toMoment(v as Moment);
								updateHirePeriodEnd(m.clone().utcOffset(m.utcOffset(), true));
							}}
							onTimezoneChange={(offMin) => {
								if (offMin && hirePeriodEnd) {
									updateHirePeriodEnd(hirePeriodEnd.clone().utcOffset(offMin, true));
								}
							}}
							minDate={minDate ? toMoment(minDate).utc() : undefined}
							maxDate={hireCapDate ?? undefined}
							showTimezone
							disableTimezone={details.tcTerms.useUTC}
							renderExtraFooter={() => (
								<>
									<Button
										type="link"
										size="small"
										disabled={hireCapDate == null}
										onClick={() => {
											const capEnd = getCappedHireEnd();

											if (capEnd == null) {
												return;
											}

											updateHirePeriodEnd(capEnd);
											hirePeriodEndPickerRef?.current?.blur();
										}}
									>
										Add remaining hire
									</Button>
									{hireCapDate == null && (
										<TooltipIcon>
											Enter the actual redelivery date to enable this option
										</TooltipIcon>
									)}
								</>
							)}
						/>
						<Divider className={styles.hireEndDivider}>or</Divider>
						<NumericInput
							asAsync
							value={(hirePeriodStart == null || rawHirePeriodEnd == null ?
								undefined :
								round(
									toMoment(rawHirePeriodEnd).diff(hirePeriodStart, 'days', true),
									fixtureDetails.hireDaysDecimals ?? 6,
								)
							)}
							onChange={(value) => {
								let newDate;

								if (value == null) {
									newDate = toMoment(hirePeriodStart);
								} else if (Number.isInteger(value)) {
									newDate = toMoment(hirePeriodStart).add(value, 'days');
								} else {
									newDate = toMoment(hirePeriodStart).add(value * 24, 'hours');
								}

								const capEnd = getCappedHireEnd();

								if (capEnd != null && newDate.isAfter(capEnd)) {
									newDate = capEnd;
								}

								updateHirePeriodEnd(newDate);
							}}
							addonAfter="Days"
							allowNegative={false}
						/>
					</>
				),
			},
			{
				hide: !includeHirePeriod,
				label: (
					<>
						Split Hire Period
						<TooltipIcon>
							If the hire invoice should include multiple separate hire rates,
							choose a date to split the hire period.
						</TooltipIcon>
					</>
				),
				content: (
					<DatePicker
						allowClear
						time
						value={splitHirePeriodValue}
						onChange={(v) => {
							if (!v || Array.isArray(v)) {
								setHirePeriodSplitDate(null);

								return;
							}

							const pickedDate = toMoment(v);

							if (details.tcTerms.useUTC) {
								setHirePeriodSplitDate(pickedDate.clone().utcOffset(0, true));

								return;
							}

							const firstPort = details.tcTerms.deliveryPortsOrRanges?.[0];
							const offsetMinutes = getUtcOffset(firstPort, pickedDate) ?? 0;

							setHirePeriodSplitDate(pickedDate.clone().utcOffset(offsetMinutes, true));
						}}
						onTimezoneChange={(offMin) => {
							if (!Number.isFinite(offMin)) {
								return;
							}

							if (!hirePeriodSplitDate) {
								return;
							}

							setHirePeriodSplitDate(hirePeriodSplitDate.clone().utcOffset(offMin as number, true));
						}}
						minDate={primaryHirePeriod?.from != null ? toMoment(primaryHirePeriod?.from) : undefined}
						maxDate={maxDate}
						showTimezone
						disableTimezone={details.tcTerms.useUTC}
						defaultUtcOffset={defaultUtcOffset}
					/>
				),
			},
			{
				hide: cumulative,
				label: 'Include previous items and payments',
				content: (
					<Select
						value={showPreviousItems}
						onChange={(v) => typeof v === 'boolean' && setShowPreviousItems(v)}
						options={[
							{ label: 'Do not include', value: false },
							{ label: 'Include', value: true },
						]}
					/>
				),
			},
			{
				hide: false,
				label: 'Group Hire Periods',
				content: (
					<Select
						value={groupHirePeriods}
						onChange={(v) => typeof v === 'boolean' && setGroupHirePeriods(v)}
						options={[
							{ label: 'Yes', value: true },
							{ label: 'No', value: false },
						]}
					/>
				),
			},
			{
				hide: cumulative,
				label: 'Include payment term',
				content: (
					<Select
						value={showPaymentTerms}
						onChange={(v) => typeof v === 'boolean' && setShowPaymentTerms(v)}
						options={[
							{ label: 'Include', value: true },
							{ label: 'Do not include', value: false },
						]}
					/>
				),
			},
			{
				label: 'On behalf of',
				content: (
					<Select
						value={showOnBehalfOf}
						onChange={(v) => typeof v === 'boolean' && setShowOnBehalfOf(v)}
						options={[
							{ label: 'Show', value: true },
							{ label: 'Hide', value: false },
						]}
					/>
				),
			},
			{
				hide: usesOwnBankAccount,
				label: 'Credit note',
				content: (
					<Select
						value={isCreditNote}
						onChange={(v) => {
							if (typeof v === 'boolean') {
								setIsCreditNoteTouched(true);
								setIsCreditNote(v);
							}
						}}
						options={[
							{ label: 'Yes', value: true },
							{ label: 'No', value: false },
						]}
					/>
				),
			},
			{
				hide: !details.tcTerms.useUTC,
				label: 'Use Legacy Time',
				content: (
					<Select
						value={useLegacyTime}
						onChange={(v) => typeof v === 'boolean' && setUseLegacyTime(v)}
						options={[
							{ label: 'Yes', value: true },
							{ label: 'No', value: false },
						]}
					/>
				),
			},
			{
				hide: !canChooseBankAccount,
				...getBankAccountSetting({
					currency: fixtureCurrency,
					bankAccounts: details.allBankAccounts,
					selectedBankAccount: selectedBankAccountId,
					setSelectedBankAccount: setSelectedBankAccountId,
					allowNoBankAccount: true,
				}),
			},
		],
		...(includeHirePeriod ? (
			hirePeriodSplitDate == null ? {
				'Hire Rate': getHirePeriodSettings(true),
			} : {
				'Hire Rate (1)': getHirePeriodSettings(true),
				'Hire Rate (2)': getHirePeriodSettings(false),
			}
		) : {}),
		...(details.tcTerms.ExpensesSubjectToHireDays && details.tcTerms.ExpensesSubjectToHireDays.length > 0 ?
			{ 'Expenses Subject to Hire Days': getExpensesSubjectToHireDaysSettings() } : {}),
	};

	const createBbItemSettings = (type: string) => [
		{
			label: 'Start',
			content: (
				<DatePicker
					allowClear={false}
					showNow={false}
					time
					className={styles.settingsInput}
					value={HIIBbItemsByType[type]?.from}
					onChange={(newVal) => {
						if (newVal && HIIBbItemsByType[type]) {
							updateHIIBbItemProperty(HIIBbItemsByType[type].id, 'from', newVal);
						}
					}}
				/>
			),
		},
		{
			label: 'End',
			content: (
				<DatePicker
					allowClear={false}
					showNow={false}
					time
					className={styles.settingsInput}
					value={HIIBbItemsByType[type]?.to}
					onChange={(newVal) => {
						if (newVal && HIIBbItemsByType[type]) {
							updateHIIBbItemProperty(HIIBbItemsByType[type].id, 'to', newVal);
						}
					}}
				/>
			),
		},
		{
			label: 'Rate',
			content: (
				<CurrencyInput
					currency={fixtureCurrency}
					className={styles.settingsInput}
					defaultValue={HIIBbItemsByType[type]?.amount}
					onChange={(value) => {
						if (HIIBbItemsByType[type]) {
							updateHIIBbItemProperty(HIIBbItemsByType[type].id, 'amount', value ?? 0);
						}
					}}
					addonAfter={
						details.tcTerms.hireUnit === HireUnit.MONTHS ? '/ month' : '/ day'
					}
				/>
			),
		},
	];

	const orderedBbItemTypes = [
		BareboatItemType.DRY_DOCK,
		BareboatItemType.CAPEX,
		BareboatItemType.OPEX,
		BareboatItemType.TC_HIRE,
		BareboatItemType.INTEREST,
	];

	orderedBbItemTypes.forEach((type) => {
		if (HIIBbItemsByType[type]) {
			settingsSections[type] = createBbItemSettings(type);
		}
	});

	const settings = settingsSections;

	if (fields != null) {
		const handleUpdate = async (
			id: number,
			type: CustomFieldType,
			label: string,
			value: string | number | Date,
		) => {
			await updateCustomField({
				id,
				type,
				label,
				value,
				voyageId: voyageId as number,
			});
			setCustomFieldChange(!customFieldChange);
		};

		fields.sort((a, b) => a.id - b.id).forEach((f) => {
			let field = null;

			switch (f.type) {
				case CustomFieldType.TEXT:
					field = (
						<TextArea
							defaultValue={f.value.toString()}
							onChange={(async (e) => handleUpdate(f.id, f.type, f.label, e.currentTarget.value))}
						/>
					);
					break;
				case CustomFieldType.NUMBER:
					field = (
						<Input
							defaultValue={f.value.toString()}
							onChange={(async (e) => handleUpdate(f.id, f.type, f.label, e.currentTarget.value))}
						/>
					);
					break;
				case CustomFieldType.DATEPICKER:
					field = (
						<DatePicker
							defaultValue={f.value !== '' ? (toMoment(f.value)) : null}
							time
							allowClear
							onCalendarChange={
								(newValue: Moment | Moment[]) => handleUpdate(
									f.id,
									f.type,
									f.label,
									newValue != null ? (
										newValue as Moment).toDate() : '')
							}
						/>
					);
					break;
				default:
					field = (<TextArea />);
			}

			const entry = {
				hide: false,
				label: f.label,
				content: field,
			};

			(settings as any).General.push(entry);
		});
	}

	return (
		<PdfGenerator<HireInvoiceItem>
			key={openSessionKey ?? 'closed'}
			title={(
				<>
					{(cumulative ?
						'Generate cumulative hire statement' :
						'Generate hire invoice'
					)}
					<TooltipIcon>
						To add items to the hire invoice, just drag-and-drop
						them from the left onto the PDF invoice on the right
					</TooltipIcon>
				</>
			)}
			open={open}
			onClose={onClose}
			settings={settings}
			settingsDefaultOpen={fromDateHireRate == null || cumulative}
			sections={sections}
			renderItem={(item: Items[number]) => (
				<div className={styles.item}>
					{renderItemContent(item)}
				</div>
			)}
			readOnly={readOnly}
			getConfirmStatus={getConfirmStatus}
			onUpdateInvoice={onUpdateInvoice}
			onSave={save}
			getPreview={fetchPreview}
		/>
	);
};

export default HireInvoiceForm;
